import styled from 'styled-components';
import { CALCULATOR_SERVICE } from '../../../../service/calculatorService';
import { CustomRatioImage } from '../../../../../../../../../components/image/CustomRatioImage';
import { Copy, LucideCircleAlert, LucideXCircle } from 'lucide-react';
import { useSheetItemListValueContextHook } from '../../../../../../contexts/SheetItemListContext';
import { useSettingVariablesValueContextHook } from '../../../../../../contexts/SettingVariablesContext';
import { useAnalysisVersionValueContextHook } from '../../../../../../contexts/AnalysisVersionContext';
import { ANALYSIS_VERSIONS } from '../../../../../../variables/analysisVersions';
import { STOCK_CYCLE_DATA_SERVICE } from '../../../../../../service/stockCycleDataService';
import { CustomDialog } from '../../../../../../../../../components/dialog/v1/CustomDialog';
import { customToast } from '../../../../../../../../../components/toast/custom-react-toastify/v1';
import CustomBlockButton from '../../../../../../../../../components/button/CustomBlockButton';
import { CustomPopover, useCustomPopoverHook } from '../../../../../../../../../components/popover/CustomPopover';
import { OutlierWarningPopoverFragment } from '../../../../fragments/outlier-warning-popover-fragment';
import { useState } from 'react';
import { ModalViewPurchaseMemo } from '../../../modal-view-purchase-memo';
import { usePurchaseInfosValueContextHook } from '../../../../../../contexts/PurchaseInfosContext';
import { CustomLabelInput } from '../../../../../../../../../components/input/CustomLabelInput';
import { useShoppingCartItemsQuery } from '../../../../../../Data/shoppingCartItemsReactQuery/ShoppingCartItemsReactQuery';
import { useSelectedShoppingCartStageValueContextHook } from '../../../../../../contexts/SelectedShoppingCartStageContext';
import { useOrderQtyListQuery } from '../../../../../../Data/orderQtyListReactQuery/OrderQtyListReactQuery';

const StyledBody = styled.div`
    padding: 10px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  width: 100%;
  max-height: 300px;
  border: 1px solid #efefef;
`

const StyledTable = styled.table`
  table-layout: auto;    /* 내용 길이에 맞춰 열 너비 자동 */
  white-space: nowrap;   /* 한 줄로 표시하여 열이 자동 확장 */
  border-collapse: collapse;
  background-color: #ffffff;

  /* width: 100%;  <-- 필요한 경우 제거하거나 조정
     min-width: 800px; <-- 원한다면 유지 (너무 좁아지지 않게)
  */
`;

const TableHeader = styled.thead`
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: #f8f9fa;
`

const TableHeaderCell = styled.th`
    padding: 16px;
    text-align: left;
    font-weight: 600;
    color: #495057;
    border-bottom: 2px solid #dee2e6;
    white-space: nowrap;
    font-size: 12px;
`

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f8f9fa;
  }

  &:hover {
    background-color: #e9ecef;
  }
`

const TableCell = styled.td`
  padding: 16px;
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  font-size: 12px;
  font-weight: 600;

  .thumbnail{
    width: 30px;
    height: 30px;
  }

  .danger-badge{
    display: inline-block;
    background-color: var(--redColor1);
    color: white;
    padding: 2px 4px;
    border-radius: 3px;
    font-size: 12px;
    font-weight: 600;
  }

  .view-memo-button{
        width: fit-content;
        padding: 8px 16px;
        height: fit-content;
        border-radius: 3px;
        font-size: 12px;
        background-color: #ffffff56;
        border: 1px solid #333;
        color:#888;
        font-weight: 700;
  }

  .copy-span{
    cursor: pointer;
    &:hover{
        color: #007bff !important;
    }
  }
`;

const PurchaseInfoButton = styled(CustomBlockButton)`
    min-height: 48px;

    .badge{
        background: #f1f5f9;
        color: #334155;
        font-size: 11px;
        padding: 4px 8px;
        border-radius: 20px;
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        gap: 4px;

        &.route{
            background: #eef2ff;
            color: #3730a3;
        }
    }

    .memo{
        font-size: 11px;
        background: #fff8e1;
        color: #8a6d00;
        padding: 3px 8px;
        border-radius: 4px;
        line-height: 1.3;
    }
`;

const PurchaseLabelInputBox = styled(CustomLabelInput)`
    input{
        width: 100px;
        border-radius: 5px;
        border: 1px solid #ccc;

        &:focus{
            background-color: #fff;
        }
    }
`;

export function FragmentPurchaseList({
    editableShoppingCartItems,
    setEditableShoppingCartItems,
    SAFETY_STOCK_QTY_MULTIPLIER,
    orderQtyList
}) {
    const analysisVersionValueContextHook = useAnalysisVersionValueContextHook();
    const isV3 = analysisVersionValueContextHook.analysisVersion === ANALYSIS_VERSIONS.V3;

    const settingVariablesValueContextHook = useSettingVariablesValueContextHook();
    const settingVariables = settingVariablesValueContextHook.settingVariables;

    const sheetItemListValueContextHook = useSheetItemListValueContextHook();
    const sheetItemList = sheetItemListValueContextHook?.sheetItemList;

    const alreadyPurchasedQtyList = CALCULATOR_SERVICE.getAlreadyPurchasedQtyList(sheetItemList);

    const purchaseInfosValueContextHook = usePurchaseInfosValueContextHook();

    const customPopoverHook = useCustomPopoverHook();

    const [viewPurchaseMemosModalOpen, setViewPurchaseMemosModalOpen] = useState(false);
    const [targetProductOptionDetail, setTargetProductOptionDetail] = useState(null);

    const selectedShoppingCartStageValueContextHook = useSelectedShoppingCartStageValueContextHook();
    const selectedShoppingCartStage = selectedShoppingCartStageValueContextHook?.selectedShoppingCartStage;

    const { data: shoppingCartItemsData, isLoading: shoppingCartItemsDataIsLoading } = useShoppingCartItemsQuery({
        shoppingCartStageId: selectedShoppingCartStage?._id
    });
    const shoppingCartItemCollections = shoppingCartItemsData?.data?.content;

    const handleCopyText = (text) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                customToast.success('복사되었습니다.');
            })
            .catch((error) => {
                customToast.error('복사 실패:', error);
            });
    }
    return (
        <>
            <StyledBody>
                <h3># 장바구니 담기</h3>
                <TableContainer>
                    <StyledTable>
                        <TableHeader>
                            <tr>
                                <TableHeaderCell>상품이미지</TableHeaderCell>
                                <TableHeaderCell>상품정보</TableHeaderCell>
                                <TableHeaderCell>재고수량</TableHeaderCell>
                                <TableHeaderCell>안전재고</TableHeaderCell>
                                <TableHeaderCell style={{ fontSize: '10px' }}>분석기간내 주문량 / {isV3 ? '보정' : 'IQR'}</TableHeaderCell>
                                <TableHeaderCell style={{ fontSize: '10px' }}>일평균 주문량 / {isV3 ? '보정' : 'IQR'}</TableHeaderCell>
                                <TableHeaderCell>출고가능기간</TableHeaderCell>
                                <TableHeaderCell>이미 구매됨</TableHeaderCell>
                                <TableHeaderCell>
                                    추천구매수량
                                </TableHeaderCell>
                                <TableHeaderCell>신규/보류 발주수량</TableHeaderCell>
                                <TableHeaderCell>
                                    장바구니 수량
                                </TableHeaderCell>
                                <TableHeaderCell>구매할 수량</TableHeaderCell>
                                <TableHeaderCell>구매정보 지정</TableHeaderCell>
                                <TableHeaderCell>항목 제외</TableHeaderCell>
                            </tr>
                        </TableHeader>
                        <tbody>
                            {editableShoppingCartItems?.map((shoppingCartItem, rowIndex) => {
                                const productOptionDetail = shoppingCartItem?.stockCycleData;

                                const safetyStockQty = productOptionDetail?.iqrData?.releaseQtyPerDay ? (productOptionDetail?.iqrData?.releaseQtyPerDay * SAFETY_STOCK_QTY_MULTIPLIER).toFixed(2) : 0;
                                const daysOfCanRelease = CALCULATOR_SERVICE.getDaysOfCanRelease({
                                    stockQty: productOptionDetail?.inventoryStockDto?.stockUnit,
                                    releaseQtyPerDay: productOptionDetail?.iqrData?.releaseQtyPerDay
                                });
                                const weeksOfCanRelease = Math.floor(daysOfCanRelease / 7);
                                const [purchasedQty, deliveryQty, arrivedQty, documentQty, shippingQty] = alreadyPurchasedQtyList?.map((alreadyPurchasedQty) => {
                                    let quantity = 0;
                                    const targetData = alreadyPurchasedQty?.summedArray?.find(r => r.optionCode === productOptionDetail?.productOptionDto?.code);
                                    if (targetData) {
                                        quantity = targetData?.quantity;
                                    }
                                    return quantity;
                                });

                                const totalAlreadyPurchasedQty = purchasedQty + deliveryQty + arrivedQty + documentQty + shippingQty;

                                const recommendPurchaseQty = CALCULATOR_SERVICE.getRecommendPurchaseQty({
                                    stockQty: productOptionDetail?.inventoryStockDto?.stockUnit,
                                    releaseQtyPerDay: productOptionDetail?.iqrData?.releaseQtyPerDay,
                                    targetDOS: settingVariables.targetDOS,
                                    daysOfLeadTime: settingVariables.daysOfLeadTime,
                                    safetyStockQty: safetyStockQty,
                                    totalAlreadyPurchasedQty: totalAlreadyPurchasedQty,
                                });

                                // 이상치 여부 확인 
                                const isOutlierWarning = STOCK_CYCLE_DATA_SERVICE.isOutlierWarning(productOptionDetail);

                                // 장바구니 구매목록
                                const targetShoppingCartItemCollections = shoppingCartItemCollections?.filter(item => item.shoppingCartItemDto?.productOptionId === productOptionDetail?.productOptionDto?.id);
                                const shoppingCartPurchaseQty = targetShoppingCartItemCollections?.reduce((prev, curr) => {
                                    return prev + curr.shoppingCartItemDto?.purchaseQty;
                                }, 0)

                                let orderQty = orderQtyList?.find(item => item.productOptionId === productOptionDetail?.productOptionDto?.id) || { newQty: 0, confirmQty: 0, completeQty: 0, holdQty: 0 };

                                const orderNewAndHoldQtySum = orderQty?.newQty + orderQty?.holdQty;
                                return (
                                    <TableRow key={rowIndex}>
                                        <TableCell>
                                            <div className='thumbnail'>
                                                <CustomRatioImage>
                                                    <CustomRatioImage.Image src={productOptionDetail?.productDto?.thumbnailUri} />
                                                </CustomRatioImage>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div>{productOptionDetail?.productDto?.name} [{productOptionDetail?.productDto?.productTag}]</div>
                                            <div>{productOptionDetail?.productOptionDto?.name} [{productOptionDetail?.productOptionDto?.productTag}]</div>
                                            <div><span className='copy-span' onClick={() => handleCopyText(productOptionDetail?.productOptionDto?.code)}>{productOptionDetail?.productOptionDto?.code}</span></div>
                                        </TableCell>
                                        <TableCell>
                                            {productOptionDetail?.inventoryStockDto?.stockUnit <= 0 ?
                                                <span style={{ color: 'var(--redColor1)' }}>{productOptionDetail?.inventoryStockDto?.stockUnit}</span>
                                                :
                                                <span>{productOptionDetail?.inventoryStockDto?.stockUnit}</span>
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {safetyStockQty}
                                        </TableCell>
                                        <TableCell><span style={{ color: '#808080', fontWeight: '500' }}>{productOptionDetail?.releaseQty}</span> / <span style={{ color: 'var(--blueColor1)', fontWeight: '800' }}>{productOptionDetail?.iqrData?.releaseQty}</span></TableCell>
                                        <TableCell>
                                            <div className='flex items-center gap-1'>
                                                <span><span style={{ color: '#808080', fontWeight: '500' }}>{productOptionDetail?.releaseQtyPerDay}</span> / <span style={{ color: 'var(--blueColor1)', fontWeight: '800' }}>{productOptionDetail?.iqrData?.releaseQtyPerDay}</span></span>
                                                {isOutlierWarning && <span><LucideCircleAlert size={16} color={'#ffbf00'} cursor={'pointer'} onClick={(e) => customPopoverHook.openPopover(e, { productOptionDetail: productOptionDetail, recommendPurchaseQty: recommendPurchaseQty, totalAlreadyPurchasedQty: totalAlreadyPurchasedQty })} /></span>}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className='flex items-center gap-1'>
                                                <div>{daysOfCanRelease}일 / {weeksOfCanRelease}주</div>
                                                {daysOfCanRelease < (settingVariables.daysOfLeadTime) ?
                                                    <span className='danger-badge'>위험</span>
                                                    :
                                                    <></>
                                                }
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {totalAlreadyPurchasedQty ? totalAlreadyPurchasedQty : '0'}
                                        </TableCell>
                                        <TableCell>
                                            {recommendPurchaseQty > 0 ?
                                                <span style={{ color: 'var(--greenColor1)' }} className='copy-span' onClick={() => handleCopyText(recommendPurchaseQty)}>{recommendPurchaseQty}</span>
                                                :
                                                <span className='copy-span' onClick={() => handleCopyText('0')}>0</span>
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <div className='font-bold text-blue-500'>
                                                TOTAL: {orderNewAndHoldQtySum}
                                            </div>
                                            <div className='text-[10px] text-gray-500'>
                                                신규: {orderQty?.newQty} / 보류:{orderQty?.holdQty}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {shoppingCartPurchaseQty ?
                                                <span>{shoppingCartPurchaseQty}</span>
                                                :
                                                <span style={{ color: '#a0a0a0' }}>0</span>}
                                        </TableCell>
                                        <TableCell>
                                            <PurchaseLabelInputBox>
                                                <PurchaseLabelInputBox.Input
                                                    type='number'
                                                    value={shoppingCartItem?.purchaseQty ?? ''}
                                                    onChange={(e) => {
                                                        const newValue = e.target.value;
                                                        setEditableShoppingCartItems(prev => {
                                                            return prev.map((item, index) => {
                                                                if (index === rowIndex) {
                                                                    return {
                                                                        ...item,
                                                                        purchaseQty: newValue ? Number(newValue) : ''
                                                                    }
                                                                }
                                                                return item;
                                                            })
                                                        })
                                                    }}
                                                ></PurchaseLabelInputBox.Input>
                                                <PurchaseLabelInputBox.Label>수량</PurchaseLabelInputBox.Label>
                                            </PurchaseLabelInputBox>
                                        </TableCell>
                                        <TableCell>
                                            {shoppingCartItem?.purchaseInfo ?
                                                <PurchaseInfoButton
                                                    type='button'
                                                    className='view-memo-button'
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setViewPurchaseMemosModalOpen(true);
                                                        setTargetProductOptionDetail(productOptionDetail);
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', gap: 5 }}>
                                                        <span className='badge route'>{shoppingCartItem?.purchaseInfo?.purchaseRoute}</span>
                                                        <span className='badge memo'>{shoppingCartItem?.purchaseInfo?.name}</span>
                                                        {shoppingCartItem?.purchaseInfo?.defaultFlag &&
                                                            <span className='badge'># Default</span>
                                                        }
                                                    </div>
                                                </PurchaseInfoButton>
                                                :
                                                <PurchaseInfoButton
                                                    type='button'
                                                    className='view-memo-button'
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setViewPurchaseMemosModalOpen(true);
                                                        setTargetProductOptionDetail(productOptionDetail);
                                                    }}
                                                >
                                                    미지정
                                                </PurchaseInfoButton>
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <LucideXCircle
                                                cursor={'pointer'}
                                                onClick={() => {
                                                    setEditableShoppingCartItems(prev => prev.filter((_, index) => index !== rowIndex));
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                            )}
                        </tbody>
                    </StyledTable>
                </TableContainer>
            </StyledBody>

            {(customPopoverHook.anchorEl && customPopoverHook.element) &&
                <CustomPopover
                    anchorEl={customPopoverHook.anchorEl}
                    onClose={customPopoverHook.closePopover}
                    customLayout={false}
                    layoutStyle={{ width: 'fit-content', maxWidth: '400px' }}
                >
                    <OutlierWarningPopoverFragment
                        productOptionDetail={customPopoverHook.element?.productOptionDetail}
                        recommendPurchaseQty={customPopoverHook.element?.recommendPurchaseQty}
                        totalAlreadyPurchasedQty={customPopoverHook.element?.totalAlreadyPurchasedQty}
                    />
                </CustomPopover>
            }

            {(viewPurchaseMemosModalOpen && targetProductOptionDetail) &&
                <ModalViewPurchaseMemo
                    open={viewPurchaseMemosModalOpen}
                    productOptionDetail={targetProductOptionDetail}
                    onClose={() => {
                        setViewPurchaseMemosModalOpen(false);
                        setTargetProductOptionDetail(null);
                    }}
                    ableToSelect={true}
                    onSelect={(data) => {
                        console.log(data)
                        if (!data) {
                            // 미지정 선택시
                            setEditableShoppingCartItems(prev => {
                                return prev.map(item => {
                                    if (item?.stockCycleData?.productOptionDto?.id === targetProductOptionDetail?.productOptionDto?.id) {
                                        return {
                                            ...item,
                                            purchaseInfoUuid: null,
                                            purchaseInfo: null
                                        }
                                    }
                                    return item;
                                })
                            })
                        } else {
                            setEditableShoppingCartItems(prev => {
                                return prev.map(item => {
                                    if (item?.stockCycleData?.productOptionDto?.id === targetProductOptionDetail?.productOptionDto?.id) {
                                        return {
                                            ...item,
                                            purchaseInfoUuid: data?.uuid,
                                            purchaseInfo: data
                                        }
                                    }
                                    return item;
                                })
                            })
                        }
                        setViewPurchaseMemosModalOpen(false);
                        setTargetProductOptionDetail(null);
                    }}
                />
            }
        </>
    );
}